import { useState, useEffect, useCallback, useRef } from 'react';
import { getCountryData, getStatesByCountry, getZipRegexByCountry } from '../../../../libs.jsx';
import HeaderInfoTip from '../../../../components/v2/HeaderInfoTip.jsx';
import '../../../../components/v2/drawer-tabs.css';
import Drawer from '../../../../components/v2/Drawer.jsx';

/**
 * CorporationDrawer — slide-out drawer for the Add Corporation form.
 *
 * Two tabs: "New Corporation" (name + optional address) and
 * "Migrate Corporation" (name + 2.0 GUID).
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   onSave: (data: object) => Promise<void>,
 * }} props
 */
const CorporationDrawer = ({ isOpen, onClose, onSave }) => {
  const drawerRef = useRef(null);

  // Tab state
  const [activeTab, setActiveTab] = useState('new'); // 'new' | 'migrate'

  // New Corporation form state
  const [corpName, setCorpName] = useState('');
  const [country, setCountry] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipcode, setZipcode] = useState('');

  // Migrate Corporation form state
  const [migrateName, setMigrateName] = useState('');
  const [corpGuid, setCorpGuid] = useState('');

  const [saving, setSaving] = useState(false);

  // Reset form on open
  useEffect(() => {
    if (isOpen) {
      setActiveTab('new');
      setCorpName('');
      setCountry('');
      setAddress('');
      setCity('');
      setState('');
      setZipcode('');
      setMigrateName('');
      setCorpGuid('');
      setSaving(false);
    }
  }, [isOpen]);

  // Focus first input on open
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const firstInput = drawerRef.current?.querySelector('input, select');
        firstInput?.focus();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Reset state/zip when country changes
  useEffect(() => {
    setState('');
    setZipcode('');
  }, [country]);

  // Validation
  const namePattern = /^[0-9a-zA-Z_ \-]+$/;
  const guidPattern = /^[0-9a-zA-Z_ \-]+$/;

  const isNewNameValid = corpName.trim() && namePattern.test(corpName.trim());
  const isMigrateNameValid = migrateName.trim() && namePattern.test(migrateName.trim());
  const isGuidValid = corpGuid.trim() && guidPattern.test(corpGuid.trim());

  const zipRegex = country ? getZipRegexByCountry(country) : '';
  const isZipValid = !zipcode.trim() || !zipRegex || new RegExp(`^${zipRegex}$`).test(zipcode.trim());

  // New Corporation: only Corporate Name is required; address fields are optional
  const isNewFormValid = !!isNewNameValid;
  const isMigrateFormValid = !!isMigrateNameValid && !!isGuidValid;

  const isFormValid = activeTab === 'new' ? isNewFormValid : isMigrateFormValid;

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (!isFormValid) return;
      setSaving(true);

      const formData =
        activeTab === 'new'
          ? {
              corporate_name: corpName.trim(),
              corporate_guid: '',
              address: address.trim(),
              city: city.trim(),
              state,
              country,
              zipcode: zipcode.trim(),
              migrate: false,
            }
          : {
              corporate_name: migrateName.trim(),
              corporate_guid: corpGuid.trim(),
              address: '',
              city: '',
              state: '',
              country: '',
              zipcode: '',
              migrate: true,
            };

      onSave(formData).finally(() => setSaving(false));
    },
    [isFormValid, activeTab, corpName, country, address, city, state, zipcode, migrateName, corpGuid, onSave]
  );

  // Data helpers
  const countries = getCountryData();
  const states = country ? getStatesByCountry(country) || [] : [];

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      panelRef={drawerRef}
      ariaLabel="Add Corporation"
      headerStart={(
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <h2 className="dp-drawer__title">Add Corporation</h2>
          <HeaderInfoTip
            label="About 5.0 and 4.0 corporations"
            spaced
            title="5.0 Corporation"
            body="Should only be created if all campuses under this corporation will be exclusively 5.0. Cannot support 4.0 campuses once created."
            sections={[
              { heading: '4.0 Corporation', body: 'Supports a mix of both 4.0 and 5.0 campuses. Must first be set up in the legacy NOC system and then migrated over using its GUID.' },
            ]}
          />
        </div>
      )}
      subheader={(
        <div className="dp-drawer-tabs">
          <button
            type="button"
            className={`dp-drawer-tab ${activeTab === 'new' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('new')}
          >
            5.0 Corporation
          </button>
          <button
            type="button"
            className={`dp-drawer-tab ${activeTab === 'migrate' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('migrate')}
          >
            4.0 Corporation
          </button>
        </div>
      )}
      rawBody
    >
      {/* Form */}
      <form onSubmit={handleSubmit} className="dp-drawer__body">
        {/* === NEW CORPORATION TAB === */}
        {activeTab === 'new' ? (
          <>
            {/* Corporate Name */}
            <div className="dp-drawer__field">
              <label htmlFor="crp-name" className="dp-drawer__label">
                Corporate Name <span className="dp-drawer__required">*</span>
              </label>
              <input
                id="crp-name"
                type="text"
                value={corpName}
                onChange={(e) => setCorpName(e.target.value)}
                placeholder="Corporate name"
                className={`dp-drawer__input ${corpName && !isNewNameValid ? 'is-invalid' : ''}`}
                required
              />
              {corpName && !isNewNameValid ? (
                <p className="dp-drawer__error">Only letters, numbers, spaces, dashes, and underscores</p>
              ) : null}
            </div>

            {/* Country */}
            <div className="dp-drawer__field">
              <label htmlFor="crp-country" className="dp-drawer__label">Country</label>
              <select
                id="crp-country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="dp-drawer__select"
              >
                <option value="">Select country...</option>
                {countries.map(([code, name]) => (
                  <option key={code} value={code}>{name}</option>
                ))}
              </select>
            </div>

            {/* Street Address */}
            <div className="dp-drawer__field">
              <label htmlFor="crp-address" className="dp-drawer__label">Street Address</label>
              <input
                id="crp-address"
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Street address"
                className="dp-drawer__input"
              />
            </div>

            {/* City + State + Zip (row) */}
            <div className="crp-drawer-address-row">
              <div className="dp-drawer__field" style={{ flex: '2' }}>
                <label htmlFor="crp-city" className="dp-drawer__label">City</label>
                <input
                  id="crp-city"
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="City"
                  className="dp-drawer__input"
                />
              </div>
              <div className="dp-drawer__field" style={{ flex: '1' }}>
                <label htmlFor="crp-state" className="dp-drawer__label">State</label>
                <select
                  id="crp-state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="dp-drawer__select"
                  disabled={!country}
                >
                  <option value="">...</option>
                  {states.map(([code]) => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </select>
              </div>
              <div className="dp-drawer__field" style={{ flex: '1' }}>
                <label htmlFor="crp-zip" className="dp-drawer__label">Zip Code</label>
                <input
                  id="crp-zip"
                  type="text"
                  value={zipcode}
                  onChange={(e) => setZipcode(e.target.value)}
                  placeholder="Zip"
                  className={`dp-drawer__input ${zipcode && !isZipValid ? 'is-invalid' : ''}`}
                />
              </div>
            </div>
          </>
        ) : null}

        {/* === MIGRATE CORPORATION TAB === */}
        {activeTab === 'migrate' ? (
          <>
            {/* Corporate Name */}
            <div className="dp-drawer__field">
              <label htmlFor="crp-migrate-name" className="dp-drawer__label">
                Corporate Name <span className="dp-drawer__required">*</span>
              </label>
              <input
                id="crp-migrate-name"
                type="text"
                value={migrateName}
                onChange={(e) => setMigrateName(e.target.value)}
                placeholder="Corporate name"
                className={`dp-drawer__input ${migrateName && !isMigrateNameValid ? 'is-invalid' : ''}`}
                required
              />
              {migrateName && !isMigrateNameValid ? (
                <p className="dp-drawer__error">Only letters, numbers, spaces, dashes, and underscores</p>
              ) : null}
            </div>

            {/* Corporate 2.0 GUID */}
            <div className="dp-drawer__field">
              <label htmlFor="crp-guid" className="dp-drawer__label">
                Corporate 2.0 GUID <span className="dp-drawer__required">*</span>
              </label>
              <input
                id="crp-guid"
                type="text"
                value={corpGuid}
                onChange={(e) => setCorpGuid(e.target.value)}
                placeholder="Corporate 2.0 GUID"
                className={`dp-drawer__input ${corpGuid && !isGuidValid ? 'is-invalid' : ''}`}
                required
              />
              {corpGuid && !isGuidValid ? (
                <p className="dp-drawer__error">Only letters, numbers, spaces, dashes, and underscores</p>
              ) : null}
            </div>
          </>
        ) : null}

        {/* Footer */}
        <div className="dp-drawer__footer">
          <button
            type="button"
            onClick={onClose}
            className="dp-btn dp-btn--secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isFormValid || saving}
            className="dp-btn dp-btn--primary"
          >
            {saving ? 'Adding...' : activeTab === 'new' ? 'Add Corporation' : 'Migrate Corporation'}
          </button>
        </div>
      </form>
    </Drawer>
  );
};

export default CorporationDrawer;
